import {
  ADMIN_ROLE_ID,
  BA_ADMIN,
  SUPER_BA_ADMIN,
  CA_ANALYST_ROLE_ID,
  COUNTRYPIC_ROLE_ID,
} from 'library/api-common/src/lib/utils/appliationConstants';
import { Observable, Subscription, timer } from 'rxjs';
import { switchMap, take } from 'rxjs/operators';

/** Re-sync when materialized_view_sync.last_synced_at is older than this. */
export const MV_SYNC_STALE_MS = 4 * 60 * 60 * 1000;

/** How often to check whether the 4-hour threshold has passed (page open). */
export const MV_SYNC_CHECK_INTERVAL_MS = 5 * 60 * 1000;

/** Refresh the displayed last-synced time from the server. */
export const MV_SYNC_STATUS_POLL_MS = 60 * 1000;

/** Poll after starting refresh until materialized_view_sync updates. */
const MV_SYNC_COMPLETION_POLL_MS = 10 * 1000;
const MV_SYNC_COMPLETION_MAX_POLLS = 180;

export const MV_SYNC_ALLOWED_ROLE_IDS: readonly number[] = [
  ADMIN_ROLE_ID,
  BA_ADMIN, SUPER_BA_ADMIN,
  CA_ANALYST_ROLE_ID,
  COUNTRYPIC_ROLE_ID,
];

export function canTriggerMvSync(roleId: number | null | undefined): boolean {
  return roleId != null && MV_SYNC_ALLOWED_ROLE_IDS.includes(roleId);
}

export interface MvSyncStatusPayload {
  lastSyncTime: string | null;
  syncedBy?: number | null;
}

/** Unwrap `{ result: { lastSyncTime } }` from survey/row-data API interceptors. */
export function extractMvSyncStatus(res: unknown): MvSyncStatusPayload {
  const payload =
    res != null &&
    typeof res === 'object' &&
    'result' in res &&
    (res as { result?: unknown }).result != null
      ? (res as { result: unknown }).result
      : res;

  if (payload == null || typeof payload !== 'object') {
    return { lastSyncTime: null, syncedBy: null };
  }

  const row = payload as Record<string, unknown>;
  return {
    lastSyncTime: normalizeMvSyncTime(
      row['lastSyncTime'] ?? row['last_synced_at'],
    ),
    syncedBy:
      (row['syncedBy'] as number | null | undefined) ??
      (row['synced_by'] as number | null | undefined) ??
      null,
  };
}

export function normalizeMvSyncTime(
  value: unknown,
): string | null {
  if (value == null || value === '') {
    return null;
  }
  if (value instanceof Date) {
    return value.toISOString();
  }
  if (typeof value === 'string') {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString();
  }
  return null;
}

export function formatMvLastSyncedDateTime(
  lastSyncedTime: string | null | undefined,
): string {
  if (!lastSyncedTime) {
    return 'Never synced';
  }
  const date = new Date(lastSyncedTime);
  if (Number.isNaN(date.getTime())) {
    return 'Never synced';
  }
  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function isMvSyncStale(
  lastSyncedTime: string | null | undefined,
  staleMs: number = MV_SYNC_STALE_MS,
): boolean {
  if (!lastSyncedTime) {
    return false;
  }
  const date = new Date(lastSyncedTime);
  if (Number.isNaN(date.getTime())) {
    return true;
  }
  return Date.now() - date.getTime() >= staleMs;
}

function isNewerSyncTime(
  previous: string | null,
  next: string | null | undefined,
): boolean {
  if (!next) {
    return false;
  }
  if (!previous) {
    return true;
  }
  return new Date(next).getTime() > new Date(previous).getTime();
}

export interface MvSyncUiCoordinatorConfig {
  roleId?: number | null;
  onStateChange?: () => void;
}

export interface MvSyncTriggerOptions {
  background?: boolean;
  onSuccess?: (lastSyncTime: string) => void;
  onError?: (err: unknown) => void;
}

export class MvSyncUiCoordinator {
  lastSyncedTime: string | null = null;
  lastSyncedDisplayText = 'Never synced';
  /** True only while the user clicked Sync Data and refresh is in progress. */
  isSyncingData = false;

  canShowSyncButton = false;

  private readonly config: MvSyncUiCoordinatorConfig;
  private syncInProgress = false;
  private staleCheckInterval: ReturnType<typeof setInterval> | null = null;
  private statusPollInterval: ReturnType<typeof setInterval> | null = null;
  private statusSub: Subscription | null = null;
  private syncSub: Subscription | null = null;
  private completionPollSub: Subscription | null = null;
  private getStatusFn: (() => Observable<unknown>) | null = null;
  private refreshFn: (() => Observable<unknown>) | null = null;
  private onAutoSyncSuccess?: (lastSyncTime: string) => void;

  constructor(config: MvSyncUiCoordinatorConfig = {}) {
    this.config = config;
    this.updateRoleAccess(config.roleId);
  }

  setRoleId(roleId: number | null | undefined): void {
    this.config.roleId = roleId;
    this.updateRoleAccess(roleId);
    this.notifyStateChange();
  }

  destroy(): void {
    this.clearStaleCheckInterval();
    this.clearStatusPollInterval();
    this.statusSub?.unsubscribe();
    this.syncSub?.unsubscribe();
    this.completionPollSub?.unsubscribe();
  }

  /**
   * Load last sync from materialized_view_sync (via syncStatus API only).
   * Does not start a refresh on page visit.
   */
  loadStatus(
    getStatus: () => Observable<unknown>,
    refreshFn: () => Observable<unknown>,
    options?: { onAutoSyncSuccess?: (lastSyncTime: string) => void },
  ): void {
    this.getStatusFn = getStatus;
    this.refreshFn = refreshFn;
    this.onAutoSyncSuccess = options?.onAutoSyncSuccess;
    this.fetchStatusFromServer();
    this.startStatusPollInterval();
    this.startStaleCheckInterval();
  }

  /** Manual Sync Data — runs refresh in background; table load is unaffected. */
  triggerSync(options?: MvSyncTriggerOptions): void {
    this.startRefresh({
      background: false,
      onSuccess: options?.onSuccess,
      onError: options?.onError,
    });
  }

  private fetchStatusFromServer(): void {
    if (!this.getStatusFn) {
      return;
    }
    this.statusSub?.unsubscribe();
    this.statusSub = this.getStatusFn().subscribe({
      next: (res) => {
        this.lastSyncedTime = extractMvSyncStatus(res).lastSyncTime;
        this.updateDisplayText();
        this.notifyStateChange();
      },
      error: () => {
        this.lastSyncedTime = null;
        this.updateDisplayText();
        this.notifyStateChange();
      },
    });
  }

  private startStatusPollInterval(): void {
    this.clearStatusPollInterval();
    this.statusPollInterval = setInterval(() => {
      this.fetchStatusFromServer();
    }, MV_SYNC_STATUS_POLL_MS);
  }

  private startStaleCheckInterval(): void {
    this.clearStaleCheckInterval();
    this.staleCheckInterval = setInterval(() => {
      this.tryScheduledAutoSync();
    }, MV_SYNC_CHECK_INTERVAL_MS);
  }

  /** Auto sync only when 4+ hours since last_synced_at (eligible roles, page open). */
  private tryScheduledAutoSync(): void {
    if (
      !this.canShowSyncButton ||
      this.syncInProgress ||
      !isMvSyncStale(this.lastSyncedTime)
    ) {
      return;
    }
    this.startRefresh({
      background: true,
      onSuccess: (lastSyncTime) => this.onAutoSyncSuccess?.(lastSyncTime),
    });
  }

  private startRefresh(options: {
    background: boolean;
    onSuccess?: (lastSyncTime: string) => void;
    onError?: (err: unknown) => void;
  }): void {
    if (!this.canShowSyncButton || !this.refreshFn || this.syncInProgress) {
      return;
    }

    const previousSyncTime = this.lastSyncedTime;
    this.syncInProgress = true;
    if (!options.background) {
      this.isSyncingData = true;
      this.notifyStateChange();
    }

    this.syncSub?.unsubscribe();
    this.syncSub = this.refreshFn().subscribe({
      next: () => {
        this.pollUntilSyncComplete(previousSyncTime, {
          background: options.background,
          onSuccess: options.onSuccess,
          onError: options.onError,
        });
      },
      error: (err) => {
        this.syncInProgress = false;
        if (!options.background) {
          this.isSyncingData = false;
          options.onError?.(err);
        }
        this.notifyStateChange();
      },
    });
  }

  private pollUntilSyncComplete(
    previousSyncTime: string | null,
    options: {
      background: boolean;
      onSuccess?: (lastSyncTime: string) => void;
      onError?: (err: unknown) => void;
    },
  ): void {
    if (!this.getStatusFn) {
      this.syncInProgress = false;
      if (!options.background) {
        this.isSyncingData = false;
      }
      this.notifyStateChange();
      return;
    }

    this.completionPollSub?.unsubscribe();
    this.completionPollSub = timer(0, MV_SYNC_COMPLETION_POLL_MS)
      .pipe(
        take(MV_SYNC_COMPLETION_MAX_POLLS),
        switchMap(() => this.getStatusFn!()),
      )
      .subscribe({
        next: (res) => {
          const nextTime = extractMvSyncStatus(res).lastSyncTime;
          if (isNewerSyncTime(previousSyncTime, nextTime)) {
            this.completionPollSub?.unsubscribe();
            this.lastSyncedTime = nextTime;
            this.updateDisplayText();
            this.syncInProgress = false;
            if (!options.background) {
              this.isSyncingData = false;
            }
            options.onSuccess?.(nextTime!);
            this.notifyStateChange();
          }
        },
        complete: () => {
          if (this.syncInProgress) {
            this.syncInProgress = false;
            if (!options.background) {
              this.isSyncingData = false;
              options.onError?.(new Error('Sync timed out'));
            }
            this.fetchStatusFromServer();
            this.notifyStateChange();
          }
        },
      });
  }

  private clearStaleCheckInterval(): void {
    if (this.staleCheckInterval) {
      clearInterval(this.staleCheckInterval);
      this.staleCheckInterval = null;
    }
  }

  private clearStatusPollInterval(): void {
    if (this.statusPollInterval) {
      clearInterval(this.statusPollInterval);
      this.statusPollInterval = null;
    }
  }

  private updateRoleAccess(roleId: number | null | undefined): void {
    this.canShowSyncButton = canTriggerMvSync(roleId);
  }

  private updateDisplayText(): void {
    this.lastSyncedDisplayText = formatMvLastSyncedDateTime(this.lastSyncedTime);
  }

  private notifyStateChange(): void {
    this.config.onStateChange?.();
  }
}
